import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default markers in Leaflet with Vite
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import { getMapTilerStreetTileLayerConfig } from '../../../config/mapTiles';

const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

const ZIMBABWE_CENTER: [number, number] = [-19.0154, 29.1549];
const ZIMBABWE_ZOOM = 6;
const FARMER_ZOOM = 12;

interface LocationData {
  id: string;
  name: string;
  lat: number;
  lng: number;
  status: 'valid' | 'invalid' | 'pending';
  farmer?: {
    name: string;
    phone?: string;
    area?: string;
  };
}

interface LocationMapProps {
  locations: LocationData[];
  height?: string;
  className?: string;
  center?: { lat: number; lng: number };
  onClick?: (lat: number, lng: number) => void;
}

export function LocationMap({ locations, height = '400px', className = '', center, onClick }: LocationMapProps) {
  const mapRef = useRef<L.Map | null>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!mapContainerRef.current) return;

    const mapCenter: [number, number] = center
      ? [center.lat, center.lng]
      : (locations.length > 0 && locations[0].lat && locations[0].lng)
        ? [locations[0].lat, locations[0].lng]
        : ZIMBABWE_CENTER;
    const zoom = (center || (locations.length > 0 && locations[0].lat)) ? FARMER_ZOOM : ZIMBABWE_ZOOM;

    if (!mapRef.current) {
      mapRef.current = L.map(mapContainerRef.current).setView(mapCenter, zoom);

      const tiles = getMapTilerStreetTileLayerConfig();
      L.tileLayer(tiles.url, {
        attribution: tiles.attribution,
        maxZoom: tiles.maxZoom,
      }).addTo(mapRef.current);
    } else {
      mapRef.current.setView(mapCenter, zoom);
    }

    const map = mapRef.current;

    // Clear existing markers
    map.eachLayer((layer) => {
      if (layer instanceof L.Marker) {
        map.removeLayer(layer);
      }
    });

    // Add markers for each location
    locations.forEach((location) => {
      if (location.lat && location.lng) {
        let markerColor = '#3B82F6';
        if (location.status === 'valid') markerColor = '#10B981';
        if (location.status === 'invalid') markerColor = '#EF4444';

        const customIcon = L.divIcon({
          className: 'custom-marker',
          html: `<div style="
            background-color: ${markerColor};
            width: 20px;
            height: 20px;
            border-radius: 50%;
            border: 2px solid white;
            box-shadow: 0 2px 4px rgba(0,0,0,0.3);
          "></div>`,
          iconSize: [20, 20],
          iconAnchor: [10, 10],
        });

        const marker = L.marker([location.lat, location.lng], { icon: customIcon })
          .addTo(map);

        const popupContent = `
          <div class="p-2">
            <h6 class="font-semibold text-sm">${location.name}</h6>
            <p class="text-xs text-gray-600">Status: <span class="font-medium" style="color: ${markerColor}">${location.status}</span></p>
            ${location.farmer?.phone ? `<p class="text-xs">Phone: ${location.farmer.phone}</p>` : ''}
            ${location.farmer?.area ? `<p class="text-xs">Area: ${location.farmer.area} hectares</p>` : ''}
            <p class="text-xs mt-1">Lat: ${location.lat.toFixed(4)}, Lng: ${location.lng.toFixed(4)}</p>
          </div>
        `;

        marker.bindPopup(popupContent);
      }
    });

    // Cleanup function
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [locations, center]);

  // Handle click events separately to avoid recreating the map
  useEffect(() => {
    if (!mapRef.current || !onClick) return;
    const map = mapRef.current;

    const handleClick = (e: L.LeafletMouseEvent) => {
      onClick(e.latlng.lat, e.latlng.lng);
    };

    map.on('click', handleClick);
    return () => {
      map.off('click', handleClick);
    };
  }, [onClick]);

  return (
    <div 
      ref={mapContainerRef}
      className={className}
      style={{ height, minHeight: height }}
    />
  );
}
